import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { ASSETS, LeafCorner, FlowerSpot } from "../components/Decorative";
import { Clock, Phone, MapPin, Mail, Sparkles, ArrowRight, IceCream, Snowflake, Megaphone, Facebook, Instagram, MessageCircle } from "lucide-react";

const HomePage = () => {
    const [info, setInfo] = useState(null);
    const [hours, setHours] = useState(null);
    const [todays, setTodays] = useState(null);
    const [gallery, setGallery] = useState([]);
    const [takeHome, setTakeHome] = useState(null);
    const [promos, setPromos] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/site-info").then((r) => setInfo(r.data)),
            api.get("/opening-hours").then((r) => setHours(r.data)),
            api.get("/todays-ice").then((r) => setTodays(r.data)),
            api.get("/gallery").then((r) => setGallery(r.data)),
            api.get("/take-home").then((r) => setTakeHome(r.data)),
            api.get("/promotions").then((r) => setPromos(r.data.filter((p) => p.active))),
        ]).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden garden-bg" data-testid="hero-section">
                <LeafCorner className="absolute -top-6 -left-10 w-72 h-72 opacity-90" />
                <LeafCorner className="absolute -top-6 -right-10 w-72 h-72 opacity-90" flip />
                <FlowerSpot className="absolute top-32 left-12 w-10 h-10 hidden md:block animate-sway" />
                <FlowerSpot className="absolute bottom-20 right-20 w-8 h-8 hidden md:block animate-sway" />

                <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-20 md:pt-20 md:pb-28 grid md:grid-cols-2 gap-10 items-center relative">
                    <div className="text-center md:text-left animate-fade-up">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-pastel-pink/40 text-deep-green text-sm font-medium mb-6">
                            <Sparkles className="w-3.5 h-3.5" />
                            <span>Familienfreundlicher Eis-Garten</span>
                        </div>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-deep-green leading-[1.05] tracking-tight">
                            Willkommen im
                            <span className="block font-cursive text-5xl sm:text-6xl lg:text-7xl text-garden-green-dark mt-2">Softeis Garten</span>
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-deep-green/75 max-w-xl mx-auto md:mx-0 leading-relaxed">
                            {info?.hero_subtitle || "Frisches Softeis aus dem Garten – jeden Tag eine neue Sorte."}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3 justify-center md:justify-start">
                            <Link
                                to="/speisekarte"
                                className="inline-flex items-center gap-2 bg-garden-green text-white hover:bg-garden-green-dark rounded-full px-7 py-3 font-sans font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition"
                                data-testid="hero-menu-cta"
                            >
                                Unser Angebot <ArrowRight className="w-4 h-4" />
                            </Link>
                            <a
                                href="#heute"
                                className="inline-flex items-center gap-2 bg-pastel-pink text-deep-green hover:bg-pastel-pink-dark rounded-full px-7 py-3 font-sans font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition"
                                data-testid="hero-today-cta"
                            >
                                <IceCream className="w-4 h-4" /> Heutiges Softeis
                            </a>
                        </div>
                    </div>

                    <div className="relative grid place-items-center">
                        <div className="absolute inset-0 bg-leaf-green/20 blur-3xl rounded-full" />
                        <img
                            src={ASSETS.mascot}
                            alt="Eistroll Maskottchen"
                            className="relative w-72 sm:w-96 lg:w-[28rem] object-contain animate-float drop-shadow-2xl"
                            data-testid="hero-mascot-img"
                        />
                    </div>
                </div>
            </section>

            {/* HEUTIGES EIS */}
            {info?.show_todays_ice !== false && (
                <section id="heute" className="py-20 md:py-28 cream-section" data-testid="todays-section">
                    <div className="max-w-6xl mx-auto px-5 sm:px-8">
                        <div className="text-center mb-12">
                            <span className="font-cursive text-3xl text-pastel-pink-dark">~ heute frisch ~</span>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-deep-green mt-2">
                                {todays?.headline || "Heutiges Softeis"}
                            </h2>
                            {todays?.todays_hours && (
                                <div className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-full bg-garden-green text-white shadow-md" data-testid="todays-hours">
                                    <Clock className="w-4 h-4" />
                                    <span className="font-sans font-semibold">Heute geöffnet: {todays.todays_hours}</span>
                                </div>
                            )}
                            <div className="leaf-divider"><IceCream className="w-5 h-5" /></div>
                        </div>

                        <div className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(44,76,59,0.08)] border border-cream-dark p-8 md:p-12 relative overflow-hidden">
                            <div className="absolute -top-10 -right-10 w-40 h-40 bg-pastel-pink/30 rounded-full blur-2xl" />
                            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-leaf-green/30 rounded-full blur-2xl" />

                            <div className="relative grid md:grid-cols-3 gap-8 items-center">
                                <div className="md:col-span-1 grid place-items-center">
                                    <div className="banner-ribbon font-serif text-xl">
                                        {todays?.date_label || "Heute"}
                                    </div>
                                    <img src={ASSETS.mascot} alt="" className="w-40 mt-4 -rotate-6" aria-hidden="true" />
                                </div>

                                <div className="md:col-span-2">
                                    {todays && todays.flavors?.length > 0 ? (
                                        <ul className="grid sm:grid-cols-2 gap-3">
                                            {todays.flavors.map((flavor, i) => (
                                                <li
                                                    key={i}
                                                    className="group flex items-center gap-3 p-4 rounded-2xl bg-cream-dark/60 hover:bg-leaf-green/20 transition"
                                                    data-testid={`today-flavor-${i}`}
                                                >
                                                    <IceCream className="w-5 h-5 text-garden-green-dark flex-shrink-0" />
                                                    <span className="font-sans font-medium text-lg text-deep-green">{flavor}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-deep-green/70">Heute keine besonderen Sorten eingetragen.</p>
                                    )}
                                    {todays?.note && (
                                        <p className="mt-6 font-cursive text-2xl text-garden-green-dark">{todays.note}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* AKTIONEN */}
            {info?.show_promotions !== false && promos.length > 0 && (
                <section id="aktionen" className="py-20 md:py-24" data-testid="promos-section">
                    <div className="max-w-6xl mx-auto px-5 sm:px-8">
                        <div className="text-center mb-12">
                            <span className="font-cursive text-3xl text-pastel-pink-dark">~ nicht verpassen ~</span>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-deep-green mt-2">Unsere Aktionen</h2>
                        </div>
                        <div className={`grid gap-6 ${promos.length === 1 ? "max-w-3xl mx-auto" : "md:grid-cols-2"}`}>
                            {promos.map((p) => (
                                <article
                                    key={p.id}
                                    className="bg-white rounded-3xl overflow-hidden border border-cream-dark shadow-[0_12px_40px_rgba(44,76,59,0.08)] hover-lift flex flex-col sm:flex-row"
                                    data-testid={`promo-card-${p.id}`}
                                >
                                    {p.image_url && (
                                        <img
                                            src={fileUrl(p.image_url)}
                                            alt={p.title}
                                            className="w-full sm:w-56 h-56 sm:h-auto object-cover flex-shrink-0"
                                            loading="lazy"
                                        />
                                    )}
                                    <div className="p-6 md:p-7 flex-1">
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pastel-pink/40 text-deep-green text-xs font-medium mb-3">
                                <Megaphone className="w-3 h-3" /> Aktion
                                        </div>
                                        <h3 className="font-serif text-2xl text-deep-green leading-tight">{p.title}</h3>
                                        {p.text && (
                                            <p className="mt-3 text-deep-green/75 leading-relaxed whitespace-pre-line">{p.text}</p>
                                        )}
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FÜR SPÄTER */}
            {info?.show_take_home !== false && takeHome && takeHome.flavors?.length > 0 && (
                <section id="fuer-spaeter" className="py-20 md:py-24 leaf-section" data-testid="take-home-section">
                    <div className="max-w-5xl mx-auto px-5 sm:px-8">
                        <div className="text-center mb-10">
                            <span className="font-cursive text-3xl text-pastel-pink-dark">~ zum Mitnehmen ~</span>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-deep-green mt-2">
                                {takeHome.title || "Für Später"}
                            </h2>
                        </div>

                        <div className="bg-white rounded-3xl p-8 md:p-10 border border-cream-dark shadow-[0_20px_50px_rgba(44,76,59,0.08)]">
                            {takeHome.intro_text && (
                                <div className="flex items-start gap-4 mb-8">
                                    <div className="w-12 h-12 rounded-full bg-leaf-green/30 grid place-items-center flex-shrink-0">
                                        <Snowflake className="w-5 h-5 text-deep-green" />
                                    </div>
                                    <p className="text-base sm:text-lg text-deep-green/80 leading-relaxed whitespace-pre-line">
                                        {takeHome.intro_text}
                                    </p>
                                </div>
                            )}

                            <h3 className="font-serif text-xl text-deep-green mb-4 border-t border-cream-dark pt-6">
                                Verfügbare Sorten im Froster
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {takeHome.flavors.map((f, i) => (
                                    <span
                                        key={i}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream-dark/70 text-deep-green font-medium text-sm"
                                        data-testid={`takehome-flavor-${i}`}
                                    >
                                        <Snowflake className="w-3.5 h-3.5 text-garden-green-dark" />
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ABOUT + OPENING HOURS */}
            {(info?.show_about !== false || info?.show_opening_hours !== false) && (
                <section className="py-20 md:py-24 leaf-section" data-testid="about-section">
                    <div className={`max-w-6xl mx-auto px-5 sm:px-8 grid gap-12 lg:gap-20 items-start ${
                        info?.show_about !== false && info?.show_opening_hours !== false ? "lg:grid-cols-2" : ""
                    }`}>
                        {info?.show_about !== false && (
                            <div>
                                <span className="font-cursive text-3xl text-pastel-pink-dark">~ über uns ~</span>
                                <h2 className="font-serif text-3xl sm:text-4xl text-deep-green mt-2 mb-6">
                                    {info?.about_title || "Willkommen im Softeis Garten"}
                                </h2>
                                <p className="text-base sm:text-lg text-deep-green/80 leading-relaxed whitespace-pre-line">
                                    {info?.about_text}
                                </p>
                            </div>
                        )}

                        {info?.show_opening_hours !== false && (
                            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-[0_20px_50px_rgba(44,76,59,0.08)] border border-cream-dark relative overflow-hidden">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-12 h-12 rounded-full bg-leaf-green/30 grid place-items-center">
                                        <Clock className="w-5 h-5 text-deep-green" />
                                    </div>
                                    <h3 className="font-serif text-2xl text-deep-green">Öffnungszeiten</h3>
                                </div>
                                <ul className="divide-y divide-cream-dark">
                                    {hours?.entries?.map((e, i) => (
                                        <li
                                            key={i}
                                            className="py-3 flex items-center justify-between"
                                            data-testid={`opening-hour-${i}`}
                                        >
                                            <span className="font-sans font-medium text-deep-green">{e.day}</span>
                                            <span className={`font-sans ${e.closed ? "text-pastel-pink-dark italic" : "text-deep-green/80"}`}>
                                                {e.closed ? "Geschlossen" : e.hours}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* GALLERY */}
            {info?.show_gallery !== false && (
                <section id="galerie" className="py-20 md:py-28" data-testid="gallery-section">
                    <div className="max-w-7xl mx-auto px-5 sm:px-8">
                        <div className="text-center mb-12">
                            <span className="font-cursive text-3xl text-pastel-pink-dark">~ Eindrücke ~</span>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-deep-green mt-2">Galerie</h2>
                            <p className="text-deep-green/70 mt-3 max-w-2xl mx-auto">
                                Ein kleiner Blick in unseren Garten und auf unsere leckeren Softeis-Kreationen.
                            </p>
                        </div>

                        {gallery.length === 0 ? (
                            <p className="text-center text-deep-green/60 py-10">Noch keine Bilder verfügbar.</p>
                        ) : (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                                {gallery.map((img, i) => (
                                    <figure
                                        key={img.id}
                                        className={`group relative overflow-hidden rounded-3xl bg-cream-dark hover-lift ${
                                            i % 5 === 0 ? "row-span-2" : ""
                                        }`}
                                        data-testid={`gallery-item-${i}`}
                                    >
                                        <img
                                            src={fileUrl(img.url)}
                                            alt={img.caption || "Galeriebild"}
                                            className="w-full h-full object-cover aspect-square group-hover:scale-105 transition duration-700"
                                            loading="lazy"
                                        />
                                        {img.caption && (
                                            <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-deep-green/80 to-transparent text-cream text-sm opacity-0 group-hover:opacity-100 transition">
                                                {img.caption}
                                            </figcaption>
                                        )}
                                    </figure>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* CONTACT */}
            {(info?.show_contact !== false || info?.show_map !== false || info?.show_socials !== false) && (
                <section id="kontakt" className="py-20 md:py-24 cream-section" data-testid="contact-section">
                    <div className="max-w-6xl mx-auto px-5 sm:px-8">
                        <div className="text-center">
                            <span className="font-cursive text-3xl text-pastel-pink-dark">~ besuch uns ~</span>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-deep-green mt-2 mb-10">Kontakt</h2>
                        </div>

                        {info?.show_contact !== false && (
                            <div className="grid sm:grid-cols-3 gap-6 mb-10">
                                {info?.address && (
                                    <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(info.address)}`}
                                        target="_blank" rel="noreferrer"
                                        className="bg-white rounded-3xl p-6 shadow-md hover-lift border border-cream-dark text-center"
                                        data-testid="contact-address"
                                    >
                                        <div className="w-12 h-12 mx-auto rounded-full bg-leaf-green/30 grid place-items-center mb-3">
                                            <MapPin className="w-5 h-5 text-deep-green" />
                                        </div>
                                        <div className="font-serif text-lg text-deep-green mb-1">Adresse</div>
                                        <div className="text-sm text-deep-green/75">{info.address}</div>
                                    </a>
                                )}
                                {info?.phone && (
                                    <a
                                        href={`tel:${info.phone}`}
                                        className="bg-white rounded-3xl p-6 shadow-md hover-lift border border-cream-dark text-center"
                                        data-testid="contact-phone"
                                    >
                                        <div className="w-12 h-12 mx-auto rounded-full bg-pastel-pink/40 grid place-items-center mb-3">
                                            <Phone className="w-5 h-5 text-deep-green" />
                                        </div>
                                        <div className="font-serif text-lg text-deep-green mb-1">Telefon</div>
                                        <div className="text-sm text-deep-green/75">{info.phone}</div>
                                    </a>
                                )}
                                {info?.email && (
                                    <a
                                        href={`mailto:${info.email}`}
                                        className="bg-white rounded-3xl p-6 shadow-md hover-lift border border-cream-dark text-center"
                                        data-testid="contact-email"
                                    >
                                        <div className="w-12 h-12 mx-auto rounded-full bg-soft-yellow/40 grid place-items-center mb-3">
                                            <Mail className="w-5 h-5 text-deep-green" />
                                        </div>
                                        <div className="font-serif text-lg text-deep-green mb-1">E-Mail</div>
                                        <div className="text-sm text-deep-green/75 break-all">{info.email}</div>
                                    </a>
                                )}
                            </div>
                        )}

                        {info?.show_map !== false && info?.address && (
                            <div className="rounded-3xl overflow-hidden border border-cream-dark shadow-[0_20px_50px_rgba(44,76,59,0.08)] bg-white" data-testid="contact-map">
                                <iframe
                                    title="Standort Softeis Garten"
                                    src={`https://www.google.com/maps?q=${encodeURIComponent(info.address)}&hl=de&z=15&output=embed`}
                                    width="100%"
                                    height="420"
                                    style={{ border: 0, display: "block" }}
                                    loading="lazy"
                                    allowFullScreen=""
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        )}

                        {/* Social / QR codes */}
                        {info?.show_socials !== false && (info?.facebook || info?.instagram || info?.whatsapp) && (
                            <div className="mt-14" data-testid="social-section">
                                <div className="text-center mb-8">
                                    <span className="font-cursive text-2xl text-pastel-pink-dark">~ folgen & vernetzen ~</span>
                                    <h3 className="font-serif text-2xl sm:text-3xl text-deep-green mt-1">Bleiben Sie in Kontakt</h3>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {[
                                        { url: info.facebook, qr: info.facebook_qr, icon: Facebook, label: "Facebook", key: "facebook", bg: "bg-[#1877F2]/10", iconColor: "text-[#1877F2]" },
                                        { url: info.instagram, qr: info.instagram_qr, icon: Instagram, label: "Instagram", key: "instagram", bg: "bg-gradient-to-br from-[#feda75]/25 via-[#fa7e1e]/25 to-[#d62976]/25", iconColor: "text-[#d62976]" },
                                        { url: info.whatsapp, qr: info.whatsapp_qr, icon: MessageCircle, label: "WhatsApp", key: "whatsapp", bg: "bg-[#25D366]/10", iconColor: "text-[#25D366]" },
                                    ].filter((s) => s.url).map(({ url, qr, icon: Icon, label, key, bg, iconColor }) => (
                                        <a
                                            key={key}
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="group bg-white rounded-3xl p-6 border border-cream-dark shadow-md hover-lift flex flex-col items-center text-center"
                                            data-testid={`social-card-${key}`}
                                        >
                                            {qr ? (
                                                <img
                                                    src={fileUrl(qr)}
                                                    alt={`${label} QR-Code`}
                                                    className="w-40 h-40 rounded-2xl object-contain bg-white border border-cream-dark p-2"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className={`w-40 h-40 rounded-2xl ${bg} grid place-items-center`}>
                                                    <Icon className={`w-16 h-16 ${iconColor}`} />
                                                </div>
                                            )}
                                            <div className="mt-4 flex items-center gap-2">
                                                <Icon className={`w-4 h-4 ${iconColor}`} />
                                                <span className="font-serif text-lg text-deep-green">{label}</span>
                                            </div>
                                            {qr && <p className="text-xs text-deep-green/60 mt-1">Scannen oder klicken</p>}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}
        </div>
    );
};

export default HomePage;
